// ProjectCategories.js
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FiSearch,
  FiPlus,
  FiEdit,
  FiTrash2,
  FiEye,
  FiEyeOff,
  FiFilter,
  FiArrowUp,
  FiArrowDown,
  FiCalendar,
  FiCheck,
  FiX,
  FiFolder,
  FiActivity,
  FiArchive,
  FiGlobe,
} from "react-icons/fi";
import {
  FaRegFolder,
  FaRegCheckCircle,
  FaRegTimesCircle,
} from "react-icons/fa";

const ProjectCategoriesManagemnet = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [viewingCategory, setViewingCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortConfig, setSortConfig] = useState({
    key: "cr_date",
    direction: "desc",
  });
  const [processing, setProcessing] = useState(false);

  const [formData, setFormData] = useState({
    category_name: "",
    status: "active",
  });

  const API_BASE = "https://dev.mithahomes.com/backend/api/project";

  // Statistics
  const stats = {
    total: categories.length,
    active: categories.filter((c) => c.status === "active").length,
    inactive: categories.filter((c) => c.status === "inactive").length,
    recent: categories.filter((c) => {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return new Date(c.cr_date) > weekAgo;
    }).length,
  };

  // Fetch all categories
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/all`);
      if (response.data.success) {
        setCategories(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("❌ Error loading categories");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // Filter categories
  const filteredCategories = categories.filter((category) => {
    const matchesSearch =
      category.category_name
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      category.pcid?.toString().includes(searchTerm);
    const matchesStatus =
      filterStatus === "all" || category.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // Sort categories
  const sortedCategories = React.useMemo(() => {
    if (!sortConfig.key) return filteredCategories;

    return [...filteredCategories].sort((a, b) => {
      // Handle date sorting
      if (sortConfig.key === "cr_date" || sortConfig.key === "update_time") {
        const dateA = new Date(a[sortConfig.key]);
        const dateB = new Date(b[sortConfig.key]);
        return sortConfig.direction === "asc" ? dateA - dateB : dateB - dateA;
      }

      // Handle numeric sorting
      if (sortConfig.key === "pcid") {
        return sortConfig.direction === "asc"
          ? a.pcid - b.pcid
          : b.pcid - a.pcid;
      }

      // Handle string sorting
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [filteredCategories, sortConfig]);

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction:
        sortConfig.key === key && sortConfig.direction === "asc"
          ? "desc"
          : "asc",
    });
  };

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      category_name: "",
      status: "active",
    });
    setEditingCategory(null);
  };

  // Validate form before submission
  const validateForm = () => {
    if (!formData.category_name.trim()) {
      toast.error("❌ Please enter a category name");
      return false;
    }
    if (formData.category_name.trim().length < 2) {
      toast.error("❌ Category name must be at least 2 characters long");
      return false;
    }
    if (formData.category_name.trim().length > 100) {
      toast.error("❌ Category name must be less than 100 characters");
      return false;
    }
    return true;
  };

  // ✅ Add new category or update existing
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setProcessing(true);

      let response;
      if (editingCategory) {
        // Update category
        response = await axios.put(
          `${API_BASE}/all/${editingCategory.pcid}`,
          formData,
        );
      } else {
        // Create new category
        response = await axios.post(`${API_BASE}/add`, formData);
      }

      if (response.data.success) {
        toast.success(
          `✅ ${editingCategory ? "Category updated" : "Category added"} successfully!`,
        );
        setShowModal(false);
        resetForm();
        fetchCategories();
      } else {
        throw new Error(response.data.message || "Operation failed");
      }
    } catch (error) {
      console.error("Error saving category:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Error saving category";
      if (error.response?.status === 409) {
        toast.error("❌ Category name already exists");
      } else {
        toast.error(`❌ ${errorMessage}`);
      }
    } finally {
      setProcessing(false);
    }
  };

  // ✅ Edit category
  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      category_name: category.category_name || "",
      status: category.status || "active",
    });
    setShowModal(true);
  };

  // View category details
  const handleView = (category) => {
    setViewingCategory(category);
    setShowViewModal(true);
  };

  // ✅ Toggle status
  const toggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    try {
      const response = await axios.put(`${API_BASE}/all/${id}`, {
        status: newStatus,
      });

      if (response.data.success) {
        toast.success(
          `📝 Category ${newStatus === "active" ? "activated" : "deactivated"}!`,
        );
        fetchCategories();
      } else {
        throw new Error(response.data.message || "Update failed");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Error updating status";
      toast.error(`❌ ${errorMessage}`);
    }
  };

  // ✅ Delete category
  const handleDelete = async (id) => {
    if (
      window.confirm(
        "Are you sure you want to delete this category? This action cannot be undone.",
      )
    ) {
      try {
        const response = await axios.delete(`${API_BASE}/del/${id}`);

        if (response.data.success) {
          toast.success("🗑️ Category deleted successfully!");
          fetchCategories();
        } else {
          throw new Error(response.data.message || "Delete failed");
        }
      } catch (error) {
        console.error("Error deleting category:", error);
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Error deleting category";
        toast.error(`❌ ${errorMessage}`);
      }
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Format time
  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get status color and icon
  const getStatusConfig = (status) => {
    if (status === "active") {
      return {
        background: "#d1fae5",
        color: "#065f46",
        border: "#a7f3d0",
        icon: <FaRegCheckCircle />,
        text: "Active",
      };
    } else {
      return {
        background: "#fee2e2",
        color: "#dc2626",
        border: "#fecaca",
        icon: <FaRegTimesCircle />,
        text: "Inactive",
      };
    }
  };

  // Get category icon based on name
  const getCategoryIcon = (categoryName) => {
    const name = categoryName.toLowerCase();
    if (name.includes("residential") || name.includes("home")) {
      return <FiFolder style={{ color: "#667eea", fontSize: "1.2rem" }} />;
    } else if (name.includes("commercial") || name.includes("office")) {
      return <FiGlobe style={{ color: "#10b981", fontSize: "1.2rem" }} />;
    } else if (name.includes("industrial")) {
      return <FiActivity style={{ color: "#f59e0b", fontSize: "1.2rem" }} />;
    } else if (name.includes("renovation") || name.includes("remodel")) {
      return <FiArchive style={{ color: "#ef4444", fontSize: "1.2rem" }} />;
    } else {
      return <FaRegFolder style={{ color: "#6b7280", fontSize: "1.2rem" }} />;
    }
  };

  return (
    <div
      style={{
        maxWidth: "1200px",
        margin: "0 auto",
        padding: "20px",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        background: "#f8fafc",
        minHeight: "100vh",
      }}
    >
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "30px",
          flexWrap: "wrap",
          gap: "20px",
        }}
      >
        <div>
          <h1
            style={{
              fontSize: "2.5rem",
              fontWeight: "700",
              color: "#1e293b",
              margin: "0 0 8px 0",
            }}
          >
            Project Categories
          </h1>
          <p
            style={{
              fontSize: "1.1rem",
              color: "#64748b",
              margin: "0",
            }}
          >
            Manage project categories and their status
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            border: "none",
            padding: "12px 24px",
            borderRadius: "12px",
            fontSize: "1rem",
            fontWeight: "600",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)",
            transition: "all 0.3s ease",
          }}
          onMouseOver={(e) => {
            e.target.style.transform = "translateY(-2px)";
            e.target.style.boxShadow = "0 8px 25px rgba(102, 126, 234, 0.4)";
          }}
          onMouseOut={(e) => {
            e.target.style.transform = "translateY(0)";
            e.target.style.boxShadow = "0 4px 15px rgba(102, 126, 234, 0.3)";
          }}
        >
          <FiPlus style={{ fontSize: "1.1rem" }} />
          Add New Category
        </button>
      </div>

      {/* Statistics Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: "20px",
          marginBottom: "30px",
        }}
      >
        {/* Total Categories */}
        <div
          style={{
            background: "white",
            padding: "25px",
            borderRadius: "16px",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            display: "flex",
            alignItems: "center",
            gap: "20px",
            transition: "transform 0.3s ease, box-shadow 0.3s ease",
          }}
          onMouseOver={(e) => {
            e.target.style.transform = "translateY(-4px)";
            e.target.style.boxShadow = "0 12px 25px rgba(0, 0, 0, 0.15)";
          }}
          onMouseOut={(e) => {
            e.target.style.transform = "translateY(0)";
            e.target.style.boxShadow = "0 4px 6px -1px rgba(0, 0, 0, 0.1)";
          }}
        >
          <div
            style={{
              width: "60px",
              height: "60px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, #667eea, #764ba2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.5rem",
              color: "white",
            }}
          >
            <FaRegFolder />
          </div>
          <div>
            <h3
              style={{
                fontSize: "2rem",
                fontWeight: "700",
                color: "#1e293b",
                margin: "0 0 4px 0",
              }}
            >
              {stats.total}
            </h3>
            <p
              style={{
                fontSize: "0.95rem",
                color: "#64748b",
                margin: "0",
              }}
            >
              Total Categories
            </p>
          </div>
        </div>

        {/* Active Categories */}
        <div
          style={{
            background: "white",
            padding: "25px",
            borderRadius: "16px",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            display: "flex",
            alignItems: "center",
            gap: "20px",
            transition: "transform 0.3s ease, box-shadow 0.3s ease",
          }}
          onMouseOver={(e) => {
            e.target.style.transform = "translateY(-4px)";
            e.target.style.boxShadow = "0 12px 25px rgba(0, 0, 0, 0.15)";
          }}
          onMouseOut={(e) => {
            e.target.style.transform = "translateY(0)";
            e.target.style.boxShadow = "0 4px 6px -1px rgba(0, 0, 0, 0.1)";
          }}
        >
          <div
            style={{
              width: "60px",
              height: "60px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, #10b981, #059669)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.5rem",
              color: "white",
            }}
          >
            <FiCheck />
          </div>
          <div>
            <h3
              style={{
                fontSize: "2rem",
                fontWeight: "700",
                color: "#1e293b",
                margin: "0 0 4px 0",
              }}
            >
              {stats.active}
            </h3>
            <p
              style={{
                fontSize: "0.95rem",
                color: "#64748b",
                margin: "0",
              }}
            >
              Active Categories
            </p>
          </div>
        </div>

        {/* Inactive Categories */}
        <div
          style={{
            background: "white",
            padding: "25px",
            borderRadius: "16px",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            display: "flex",
            alignItems: "center",
            gap: "20px",
            transition: "transform 0.3s ease, box-shadow 0.3s ease",
          }}
          onMouseOver={(e) => {
            e.target.style.transform = "translateY(-4px)";
            e.target.style.boxShadow = "0 12px 25px rgba(0, 0, 0, 0.15)";
          }}
          onMouseOut={(e) => {
            e.target.style.transform = "translateY(0)";
            e.target.style.boxShadow = "0 4px 6px -1px rgba(0, 0, 0, 0.1)";
          }}
        >
          <div
            style={{
              width: "60px",
              height: "60px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, #ef4444, #dc2626)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.5rem",
              color: "white",
            }}
          >
            <FiX />
          </div>
          <div>
            <h3
              style={{
                fontSize: "2rem",
                fontWeight: "700",
                color: "#1e293b",
                margin: "0 0 4px 0",
              }}
            >
              {stats.inactive}
            </h3>
            <p
              style={{
                fontSize: "0.95rem",
                color: "#64748b",
                margin: "0",
              }}
            >
              Inactive Categories
            </p>
          </div>
        </div>

        {/* Recent Categories (Last 7 days) */}
        <div
          style={{
            background: "white",
            padding: "25px",
            borderRadius: "16px",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            display: "flex",
            alignItems: "center",
            gap: "20px",
            transition: "transform 0.3s ease, box-shadow 0.3s ease",
          }}
          onMouseOver={(e) => {
            e.target.style.transform = "translateY(-4px)";
            e.target.style.boxShadow = "0 12px 25px rgba(0, 0, 0, 0.15)";
          }}
          onMouseOut={(e) => {
            e.target.style.transform = "translateY(0)";
            e.target.style.boxShadow = "0 4px 6px -1px rgba(0, 0, 0, 0.1)";
          }}
        >
          <div
            style={{
              width: "60px",
              height: "60px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, #f59e0b, #d97706)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.5rem",
              color: "white",
            }}
          >
            <FiCalendar />
          </div>
          <div>
            <h3
              style={{
                fontSize: "2rem",
                fontWeight: "700",
                color: "#1e293b",
                margin: "0 0 4px 0",
              }}
            >
              {stats.recent}
            </h3>
            <p
              style={{
                fontSize: "0.95rem",
                color: "#64748b",
                margin: "0",
              }}
            >
              Recent (7 days)
            </p>
          </div>
        </div>
      </div>

      {/* Controls Bar */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "20px",
          marginBottom: "25px",
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            position: "relative",
            flex: "1",
            minWidth: "300px",
          }}
        >
          <FiSearch
            style={{
              position: "absolute",
              left: "15px",
              top: "50%",
              transform: "translateY(-50%)",
              color: "#94a3b8",
              fontSize: "1.1rem",
            }}
          />
          <input
            type="text"
            placeholder="Search categories by name or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: "100%",
              padding: "12px 20px 12px 45px",
              border: "2px solid #e2e8f0",
              borderRadius: "12px",
              fontSize: "1rem",
              background: "white",
              transition: "all 0.3s ease",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#667eea";
              e.target.style.boxShadow = "0 0 0 3px rgba(102, 126, 234, 0.1)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#e2e8f0";
              e.target.style.boxShadow = "none";
            }}
          />
        </div>

        <div
          style={{
            display: "flex",
            gap: "12px",
          }}
        >
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={{
              padding: "12px 16px",
              border: "2px solid #e2e8f0",
              borderRadius: "12px",
              fontSize: "1rem",
              background: "white",
              cursor: "pointer",
              minWidth: "140px",
            }}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <button
            onClick={() => {
              setSearchTerm("");
              setFilterStatus("all");
            }}
            style={{
              padding: "12px 16px",
              border: "2px solid #e2e8f0",
              borderRadius: "12px",
              fontSize: "1rem",
              background: "white",
              cursor: "pointer",
              color: "#64748b",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              transition: "all 0.3s ease",
            }}
            onMouseOver={(e) => {
              e.target.style.background = "#f8fafc";
              e.target.style.color = "#475569";
            }}
            onMouseOut={(e) => {
              e.target.style.background = "white";
              e.target.style.color = "#64748b";
            }}
          >
            <FiX /> Clear Filters
          </button>
        </div>
      </div>

      {/* Data Table */}
      <div
        style={{
          background: "white",
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
        }}
      >
        {loading ? (
          <div
            style={{
              textAlign: "center",
              padding: "60px 40px",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                border: "4px solid #f1f5f9",
                borderTop: "4px solid #667eea",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                margin: "0 auto 20px",
              }}
            ></div>
            <p
              style={{
                color: "#64748b",
                fontSize: "1rem",
                margin: "0",
              }}
            >
              Loading categories...
            </p>
          </div>
        ) : sortedCategories.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "60px 40px",
              color: "#64748b",
            }}
          >
            <FiFolder
              style={{
                fontSize: "4rem",
                color: "#cbd5e1",
                marginBottom: "20px",
              }}
            />
            <h3
              style={{
                fontSize: "1.5rem",
                color: "#475569",
                margin: "0 0 12px 0",
              }}
            >
              No categories found
            </h3>
            <p
              style={{
                fontSize: "1rem",
                margin: "0 0 25px 0",
              }}
            >
              Try adjusting your search or filter criteria
            </p>
            <button
              onClick={() => setShowModal(true)}
              style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                border: "none",
                padding: "12px 24px",
                borderRadius: "12px",
                fontSize: "1rem",
                fontWeight: "600",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                margin: "0 auto",
              }}
            >
              <FiPlus /> Add Your First Category
            </button>
          </div>
        ) : (
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    background: "#f8fafc",
                    padding: "16px 20px",
                    textAlign: "left",
                    fontWeight: "600",
                    color: "#475569",
                    borderBottom: "2px solid #e2e8f0",
                    fontSize: "0.9rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    cursor: "pointer",
                  }}
                  onClick={() => handleSort("category_name")}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    Category Name
                    {sortConfig.key === "category_name" &&
                      (sortConfig.direction === "asc" ? (
                        <FiArrowUp />
                      ) : (
                        <FiArrowDown />
                      ))}
                  </span>
                </th>
                <th
                  style={{
                    background: "#f8fafc",
                    padding: "16px 20px",
                    textAlign: "left",
                    fontWeight: "600",
                    color: "#475569",
                    borderBottom: "2px solid #e2e8f0",
                    fontSize: "0.9rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    cursor: "pointer",
                  }}
                  onClick={() => handleSort("status")}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    Status
                    {sortConfig.key === "status" &&
                      (sortConfig.direction === "asc" ? (
                        <FiArrowUp />
                      ) : (
                        <FiArrowDown />
                      ))}
                  </span>
                </th>
                <th
                  style={{
                    background: "#f8fafc",
                    padding: "16px 20px",
                    textAlign: "left",
                    fontWeight: "600",
                    color: "#475569",
                    borderBottom: "2px solid #e2e8f0",
                    fontSize: "0.9rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    cursor: "pointer",
                  }}
                  onClick={() => handleSort("cr_date")}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    Created Date
                    {sortConfig.key === "cr_date" &&
                      (sortConfig.direction === "asc" ? (
                        <FiArrowUp />
                      ) : (
                        <FiArrowDown />
                      ))}
                  </span>
                </th>
                <th
                  style={{
                    background: "#f8fafc",
                    padding: "16px 20px",
                    textAlign: "left",
                    fontWeight: "600",
                    color: "#475569",
                    borderBottom: "2px solid #e2e8f0",
                    fontSize: "0.9rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedCategories.map((category) => {
                const statusConfig = getStatusConfig(category.status);
                return (
                  <tr
                    key={category.pcid}
                    style={{
                      transition: "background 0.3s ease",
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = "#f8fafc";
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = "white";
                    }}
                  >
                    <td
                      style={{
                        padding: "20px",
                        borderBottom: "1px solid #f1f5f9",
                        verticalAlign: "top",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                        }}
                      >
                        {getCategoryIcon(category.category_name)}
                        <div style={{ flex: "1" }}>
                          <h4
                            style={{
                              fontWeight: "600",
                              color: "#1e293b",
                              margin: "0 0 4px 0",
                              fontSize: "1rem",
                              cursor: "pointer",
                            }}
                            onClick={() => handleView(category)}
                          >
                            {category.category_name}
                          </h4>
                          <p
                            style={{
                              color: "#94a3b8",
                              fontSize: "0.875rem",
                              margin: "0",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <FiCalendar style={{ fontSize: "0.8rem" }} />
                            Updated:{" "}
                            {formatTime(
                              category.update_time || category.cr_date,
                            )}
                          </p>
                        </div>
                      </div>
                    </td>

                    <td
                      style={{
                        padding: "20px",
                        borderBottom: "1px solid #f1f5f9",
                        verticalAlign: "top",
                      }}
                    >
                      <div
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          padding: "6px 12px",
                          borderRadius: "20px",
                          fontSize: "0.8rem",
                          fontWeight: "600",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          background: statusConfig.background,
                          color: statusConfig.color,
                          border: `1px solid ${statusConfig.border}`,
                        }}
                      >
                        {statusConfig.icon}
                        {statusConfig.text}
                      </div>
                    </td>

                    <td
                      style={{
                        padding: "20px",
                        borderBottom: "1px solid #f1f5f9",
                        verticalAlign: "top",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          color: "#64748b",
                          fontSize: "0.875rem",
                        }}
                      >
                        <FiCalendar style={{ fontSize: "0.9rem" }} />
                        {formatDate(category.cr_date)}
                      </div>
                    </td>

                    <td
                      style={{
                        padding: "20px",
                        borderBottom: "1px solid #f1f5f9",
                        verticalAlign: "top",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                        }}
                      >
                        <button
                          onClick={() => handleView(category)}
                          style={{
                            background: "#3b82f6",
                            color: "white",
                            border: "none",
                            padding: "8px 12px",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "0.8rem",
                            fontWeight: "600",
                            transition: "all 0.3s ease",
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                          }}
                          title="View Details"
                        >
                          <FiEye />
                        </button>
                        <button
                          onClick={() =>
                            toggleStatus(category.pcid, category.status)
                          }
                          style={{
                            background:
                              category.status === "active"
                                ? "#f59e0b"
                                : "#10b981",
                            color: "white",
                            border: "none",
                            padding: "8px 12px",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "0.8rem",
                            fontWeight: "600",
                            transition: "all 0.3s ease",
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                          }}
                          title={
                            category.status === "active"
                              ? "Deactivate"
                              : "Activate"
                          }
                        >
                          {category.status === "active" ? (
                            <FiEyeOff />
                          ) : (
                            <FiCheck />
                          )}
                        </button>
                        <button
                          onClick={() => handleEdit(category)}
                          style={{
                            background: "#f1f5f9",
                            color: "#475569",
                            border: "1px solid #e2e8f0",
                            padding: "8px 12px",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "0.8rem",
                            fontWeight: "600",
                            transition: "all 0.3s ease",
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                          }}
                          title="Edit"
                        >
                          <FiEdit />
                        </button>
                        <button
                          onClick={() => handleDelete(category.pcid)}
                          style={{
                            background: "#ef4444",
                            color: "white",
                            border: "none",
                            padding: "8px 12px",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "0.8rem",
                            fontWeight: "600",
                            transition: "all 0.3s ease",
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                          }}
                          title="Delete"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div
          style={{
            position: "fixed",
            top: "0",
            left: "0",
            right: "0",
            bottom: "0",
            background: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: "1000",
            padding: "20px",
            backdropFilter: "blur(5px)",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "20px",
              width: "100%",
              maxWidth: "500px",
              maxHeight: "90vh",
              overflow: "hidden",
              boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25)",
              animation: "modalSlideIn 0.3s ease",
            }}
          >
            <div
              style={{
                padding: "25px 30px",
                borderBottom: "1px solid #e2e8f0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
              }}
            >
              <h2
                style={{
                  margin: "0",
                  fontSize: "1.5rem",
                  fontWeight: "600",
                }}
              >
                {editingCategory ? "Edit Category" : "Add New Category"}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                style={{
                  background: "rgba(255, 255, 255, 0.2)",
                  border: "none",
                  fontSize: "1.5rem",
                  color: "white",
                  cursor: "pointer",
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "background 0.3s ease",
                }}
                onMouseOver={(e) => {
                  e.target.style.background = "rgba(255, 255, 255, 0.3)";
                }}
                onMouseOut={(e) => {
                  e.target.style.background = "rgba(255, 255, 255, 0.2)";
                }}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSubmit} style={{ padding: "30px" }}>
              <div style={{ marginBottom: "25px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                    color: "#374151",
                    fontSize: "0.95rem",
                  }}
                >
                  Category Name *
                </label>
                <input
                  type="text"
                  name="category_name"
                  value={formData.category_name}
                  onChange={handleInputChange}
                  placeholder="Enter category name (e.g., Residential, Commercial)"
                  required
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    border: "2px solid #e2e8f0",
                    borderRadius: "8px",
                    fontSize: "1rem",
                    transition: "all 0.3s ease",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#667eea";
                    e.target.style.boxShadow =
                      "0 0 0 3px rgba(102, 126, 234, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#e2e8f0";
                    e.target.style.boxShadow = "none";
                  }}
                />
                <p
                  style={{
                    margin: "8px 0 0 0",
                    color: "#94a3b8",
                    fontSize: "0.85rem",
                  }}
                >
                  Use descriptive names like "Residential Projects", "Commercial
                  Buildings", etc.
                </p>
              </div>

              <div style={{ marginBottom: "25px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                    color: "#374151",
                    fontSize: "0.95rem",
                  }}
                >
                  Status
                </label>
                <div
                  style={{
                    display: "flex",
                    gap: "15px",
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      cursor: "pointer",
                      padding: "10px 15px",
                      borderRadius: "8px",
                      border: `2px solid ${formData.status === "active" ? "#10b981" : "#e2e8f0"}`,
                      background:
                        formData.status === "active" ? "#f0fdf4" : "white",
                      flex: "1",
                      transition: "all 0.3s ease",
                    }}
                  >
                    <input
                      type="radio"
                      name="status"
                      value="active"
                      checked={formData.status === "active"}
                      onChange={handleInputChange}
                      style={{ transform: "scale(1.2)" }}
                    />
                    <div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          color: "#065f46",
                          fontWeight: "600",
                        }}
                      >
                        <FiCheck /> Active
                      </div>
                      <p
                        style={{
                          margin: "4px 0 0 0",
                          color: "#94a3b8",
                          fontSize: "0.85rem",
                        }}
                      >
                        Category will be visible
                      </p>
                    </div>
                  </label>

                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      cursor: "pointer",
                      padding: "10px 15px",
                      borderRadius: "8px",
                      border: `2px solid ${formData.status === "inactive" ? "#ef4444" : "#e2e8f0"}`,
                      background:
                        formData.status === "inactive" ? "#fef2f2" : "white",
                      flex: "1",
                      transition: "all 0.3s ease",
                    }}
                  >
                    <input
                      type="radio"
                      name="status"
                      value="inactive"
                      checked={formData.status === "inactive"}
                      onChange={handleInputChange}
                      style={{ transform: "scale(1.2)" }}
                    />
                    <div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          color: "#dc2626",
                          fontWeight: "600",
                        }}
                      >
                        <FiX /> Inactive
                      </div>
                      <p
                        style={{
                          margin: "4px 0 0 0",
                          color: "#94a3b8",
                          fontSize: "0.85rem",
                        }}
                      >
                        Category will be hidden
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              <div
                style={{
                  paddingTop: "25px",
                  borderTop: "1px solid #e2e8f0",
                  display: "flex",
                  gap: "15px",
                  justifyContent: "flex-end",
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  style={{
                    background: "transparent",
                    color: "#64748b",
                    border: "2px solid #e2e8f0",
                    padding: "12px 25px",
                    borderRadius: "10px",
                    cursor: "pointer",
                    fontSize: "1rem",
                    fontWeight: "500",
                    transition: "all 0.3s ease",
                  }}
                  onMouseOver={(e) => {
                    e.target.style.background = "#f8fafc";
                    e.target.style.color = "#475569";
                  }}
                  onMouseOut={(e) => {
                    e.target.style.background = "transparent";
                    e.target.style.color = "#64748b";
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={processing}
                  style={{
                    background: processing
                      ? "#94a3b8"
                      : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                    border: "none",
                    padding: "12px 30px",
                    borderRadius: "10px",
                    cursor: processing ? "not-allowed" : "pointer",
                    fontSize: "1rem",
                    fontWeight: "600",
                    transition: "all 0.3s ease",
                    boxShadow: processing
                      ? "none"
                      : "0 4px 15px rgba(102, 126, 234, 0.3)",
                    opacity: processing ? 0.7 : 1,
                  }}
                  onMouseOver={(e) => {
                    if (!processing) {
                      e.target.style.transform = "translateY(-2px)";
                      e.target.style.boxShadow =
                        "0 8px 25px rgba(102, 126, 234, 0.4)";
                    }
                  }}
                  onMouseOut={(e) => {
                    if (!processing) {
                      e.target.style.transform = "translateY(0)";
                      e.target.style.boxShadow =
                        "0 4px 15px rgba(102, 126, 234, 0.3)";
                    }
                  }}
                >
                  {processing
                    ? "Processing..."
                    : editingCategory
                      ? "Update Category"
                      : "Add Category"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {showViewModal && viewingCategory && (
        <div
          style={{
            position: "fixed",
            top: "0",
            left: "0",
            right: "0",
            bottom: "0",
            background: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: "1000",
            padding: "20px",
            backdropFilter: "blur(5px)",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "20px",
              width: "100%",
              maxWidth: "500px",
              maxHeight: "90vh",
              overflow: "hidden",
              boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25)",
              animation: "modalSlideIn 0.3s ease",
            }}
          >
            <div
              style={{
                padding: "25px 30px",
                borderBottom: "1px solid #e2e8f0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
              }}
            >
              <h2
                style={{
                  margin: "0",
                  fontSize: "1.5rem",
                  fontWeight: "600",
                }}
              >
                Category Details
              </h2>
              <button
                onClick={() => setShowViewModal(false)}
                style={{
                  background: "rgba(255, 255, 255, 0.2)",
                  border: "none",
                  fontSize: "1.5rem",
                  color: "white",
                  cursor: "pointer",
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "background 0.3s ease",
                }}
                onMouseOver={(e) => {
                  e.target.style.background = "rgba(255, 255, 255, 0.3)";
                }}
                onMouseOut={(e) => {
                  e.target.style.background = "rgba(255, 255, 255, 0.2)";
                }}
              >
                &times;
              </button>
            </div>

            <div style={{ padding: "30px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "20px",
                  marginBottom: "25px",
                  paddingBottom: "25px",
                  borderBottom: "1px solid #e2e8f0",
                }}
              >
                <div
                  style={{
                    width: "70px",
                    height: "70px",
                    borderRadius: "16px",
                    background: "linear-gradient(135deg, #667eea, #764ba2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "2rem",
                    color: "white",
                  }}
                >
                  {getCategoryIcon(viewingCategory.category_name)}
                </div>
                <div>
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "6px 12px",
                      borderRadius: "20px",
                      fontSize: "0.8rem",
                      fontWeight: "600",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      background: getStatusConfig(viewingCategory.status)
                        .background,
                      color: getStatusConfig(viewingCategory.status).color,
                      border: `1px solid ${getStatusConfig(viewingCategory.status).border}`,
                      marginBottom: "10px",
                    }}
                  >
                    {getStatusConfig(viewingCategory.status).icon}
                    {getStatusConfig(viewingCategory.status).text}
                  </div>
                  <h3
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: "700",
                      color: "#1e293b",
                      margin: "0",
                    }}
                  >
                    {viewingCategory.category_name}
                  </h3>
                </div>
              </div>

              <div
                style={{
                  background: "#f8fafc",
                  borderRadius: "12px",
                  padding: "20px",
                  marginBottom: "25px",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    gap: "20px",
                  }}
                >
                  <div>
                    <h4
                      style={{
                        fontSize: "0.8rem",
                        fontWeight: "600",
                        color: "#64748b",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        margin: "0 0 8px 0",
                      }}
                    >
                      Category ID
                    </h4>
                    <p
                      style={{
                        fontSize: "1.2rem",
                        fontWeight: "600",
                        color: "#1e293b",
                        margin: "0",
                        fontFamily: "'Roboto Mono', monospace",
                      }}
                    >
                      #{viewingCategory.pcid}
                    </p>
                  </div>

                  <div>
                    <h4
                      style={{
                        fontSize: "0.8rem",
                        fontWeight: "600",
                        color: "#64748b",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        margin: "0 0 8px 0",
                      }}
                    >
                      Created Date
                    </h4>
                    <p
                      style={{
                        fontSize: "1rem",
                        fontWeight: "500",
                        color: "#1e293b",
                        margin: "0",
                      }}
                    >
                      {formatDate(viewingCategory.cr_date)}
                    </p>
                  </div>

                  <div>
                    <h4
                      style={{
                        fontSize: "0.8rem",
                        fontWeight: "600",
                        color: "#64748b",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        margin: "0 0 8px 0",
                      }}
                    >
                      Last Updated
                    </h4>
                    <p
                      style={{
                        fontSize: "1rem",
                        fontWeight: "500",
                        color: "#1e293b",
                        margin: "0",
                      }}
                    >
                      {formatDate(
                        viewingCategory.update_time || viewingCategory.cr_date,
                      )}
                    </p>
                  </div>

                  <div>
                    <h4
                      style={{
                        fontSize: "0.8rem",
                        fontWeight: "600",
                        color: "#64748b",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        margin: "0 0 8px 0",
                      }}
                    >
                      Time Created
                    </h4>
                    <p
                      style={{
                        fontSize: "1rem",
                        fontWeight: "500",
                        color: "#1e293b",
                        margin: "0",
                      }}
                    >
                      {formatTime(viewingCategory.cr_date)}
                    </p>
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  justifyContent: "center",
                }}
              >
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    handleEdit(viewingCategory);
                  }}
                  style={{
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                    border: "none",
                    padding: "12px 24px",
                    borderRadius: "10px",
                    cursor: "pointer",
                    fontSize: "1rem",
                    fontWeight: "600",
                    transition: "all 0.3s ease",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    flex: "1",
                  }}
                >
                  <FiEdit /> Edit Category
                </button>

                <button
                  onClick={() => setShowViewModal(false)}
                  style={{
                    background: "transparent",
                    color: "#64748b",
                    border: "2px solid #e2e8f0",
                    padding: "12px 24px",
                    borderRadius: "10px",
                    cursor: "pointer",
                    fontSize: "1rem",
                    fontWeight: "500",
                    transition: "all 0.3s ease",
                    flex: "1",
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: translateY(-50px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </div>
  );
};

export default ProjectCategoriesManagemnet;
